import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import {
    IGroupLayerProps,
    ILayerProps,
    ImgCategoryType,
    ImgModeType,
    LayerType,
    MaskType,
    MaterialIdsType,
    StretchOptionType,
    TextAlignType,
    TextWritingType,
    VectorType,
} from '@/version-types';
import { IServerBgLayerProps } from './server-template-types/layer';

export const DEFAULT_BACKGROUND_COLOR = '#FFFFFFFF';

export const DEFAULT_TEXT_GRADIENT = { colors: [], angle: 0 };

export const getDefaultTextGradient = () => {
    return cloneDeep(DEFAULT_TEXT_GRADIENT);
};

export const defaultBackgroundData = {
    backgroundColor: DEFAULT_BACKGROUND_COLOR,
    naturalHeight: 0,
    naturalWidth: 0,
    url: '',
    stretchOption: StretchOptionType.none,
    maskType: MaskType.none,
    backgroundGradient: null,
    imageTransform: {
        offsetX: 0,
        offsetY: 0,
        rotate: 0,
        scale: 1,
        flipH: false,
        flipV: false,
        widthRatio: 1,
        heightRatio: 1,
    },
    category: ImgCategoryType.bgImg,
    mode: ImgModeType.none,
    cutoutMaskURL: '',
    maskURL: '',
};

export const getDefaultBackgroundValue = (stageWidth: number, stageHeight: number) => {
    return {
        id: uuid(),
        layerType: LayerType.BACKGROUND_LAYER,
        width: stageWidth,
        height: stageHeight,
        left: 0,
        top: 0,
        rotate: 0,
        opacity: 1,
        lock: false,
        children: [],
        shine: false,
        hidden: false,
        overflow: false,
        isAILayer: false,
        parentId: '',
        materialIds: {},
        ...defaultBackgroundData,
        maskURL: '',

        maskType: MaskType.none,
        title: '',
        fitWidth: 0,
        fitHeight: 0,
    } as IServerBgLayerProps;
};

export const getDefaultClientBackgroundValue = (stageWidth: number, stageHeight: number) => {
    return {
        id: uuid(),
        layerType: LayerType.BACKGROUND_LAYER,
        width: stageWidth,
        height: stageHeight,
        left: 0,
        top: 0,
        rotate: 0,
        opacity: 1,
        lock: false,
        isAILayer: false,
        children: [],
        shine: false,
        hidden: false,
        overflow: false,
        parentId: '',
        materialIds: {},
        data: {
            ...cloneDeep(defaultBackgroundData),
        },
        /** 图片图层标签 */
        mode: ImgModeType.none,
        /** 分类 */
        category: ImgCategoryType.bgImg,
        title: '',
        fitWidth: 0,
        fitHeight: 0,
    } as ILayerProps;
};

export const DefaultClientImageLayer = {
    id: uuid(),
    layerType: LayerType.IMAGE_LAYER,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    rotate: 0,
    opacity: 1,
    lock: false,
    isAILayer: false,
    children: [],
    shine: true,
    hidden: false,
    overflow: false,
    parentId: '',
    materialIds: {},
    data: {
        naturalHeight: 0,
        naturalWidth: 0,
        url: '',
        stretchOption: StretchOptionType.fill,
        maskType: MaskType.none,
        effect: null,
        filter: null,
        imageTransform: {
            offsetX: 0,
            offsetY: 0,
            rotate: 0,
            scale: 1,
            flipH: false,
            flipV: false,
            widthRatio: 1,
            heightRatio: 1,
        },
        cutoutMaskURL: '',
        maskURL: '',
        hint: false,
        aiTextExtension: null,
        maskFeathering: 0,
        minimumMask: false,
    },
    /** 图片图层标签 */
    mode: ImgModeType.none,
    /** 分类 */
    category: ImgCategoryType.material,
    title: '',
    fitWidth: 0,
    fitHeight: 0,
} as ILayerProps;

export const DefaultClientGroupLayer = {
    id: uuid(),
    layerType: LayerType.GROUP_LAYER,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    rotate: 0,
    opacity: 1,
    lock: false,
    isAILayer: false,
    children: [],
    shine: true,
    hidden: false,
    overflow: false,
    parentId: '',
    materialIds: {},
    data: {
        isExtraGroup: false,
    } as IGroupLayerProps,
    /** 图片图层标签 */
    mode: ImgModeType.none,
    /** 分类 */
    category: ImgCategoryType.none,
    title: '',
    fitWidth: 0,
    fitHeight: 0,
} as ILayerProps;

export const DefaultClientTextLayer = {
    id: uuid(),
    layerType: LayerType.TEXT_LAYER,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    rotate: 0,
    opacity: 1,
    lock: false,
    isAILayer: false,
    children: [],
    shine: false,
    hidden: false,
    overflow: false,
    parentId: '',
    materialIds: {},
    data: {
        text: '',
        color: '',
        fontSize: 0,
        fontFamily: '',
        lineSpacing: 0,
        letterSpacing: 0,
        bold: false,
        italic: false,
        lineThrough: false,
        underline: false,
        textAlign: TextAlignType.CENTER,
        writingMode: TextWritingType.HORIZONTAL_TB,
        textGlow: [],
        textShadow: [],
        textStroke: [],
        textIndex: 0,
        flipH: false,
        flipV: false,
        textBackground: [],
        textGradient: getDefaultTextGradient(),
        listStyle: '',
    },
    /** 图片图层标签 */
    mode: ImgModeType.none,
    /** 分类 */
    category: ImgCategoryType.none,
    title: '',
    fitWidth: 0,
    fitHeight: 0,
} as ILayerProps;

export const DefaultClientVectorLayer = {
    id: uuid(),
    layerType: LayerType.VECTOR_LAYER,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    rotate: 0,
    opacity: 1,
    lock: false,
    children: [],
    shine: true,
    hidden: false,
    overflow: false,
    parentId: '',
    materialIds: {},
    data: {
        url: '',
        colors: [],
        naturalWidth: 0,
        naturalHeight: 0,
        vectorType: VectorType.Graph,
        fill: '',
        fillGradient: null,
        stroke: '',
        strokeGradient: null,
        strokeWidth: 0,
        strokeDasharray: '',
        strokeLinecap: '',
        radius: 0,
        blur: 0,
        strokeHidden: false,
        effect: {
            shadow: [],
            reflection: [],
        },
        stretchOption: StretchOptionType.fill,
        imageTransform: {
            offsetX: 0,
            offsetY: 0,
            rotate: 0,
            scale: 1,
            flipH: false,
            flipV: false,
            widthRatio: 1,
            heightRatio: 1,
        },
        pickerIndex: null,
    },
    /** 图片图层标签 */
    mode: ImgModeType.none,
    /** 分类 */
    category: ImgCategoryType.material,
    title: '',
    fitWidth: 0,
    fitHeight: 0,
} as ILayerProps;
